import { DEFAULT_RIGHT_PANEL_SIZE } from "@app/components/assistant/conversation/constant";
import type { AgentMessageWithStreaming } from "@app/components/assistant/conversation/types";
import { useActiveConversationId } from "@app/hooks/useActiveConversationId";
import { useHashParam } from "@app/hooks/useHashParams";
import { useScopedPodUiPreferences } from "@app/hooks/useScopedUIPreferences";
import { useIsMobile } from "@app/lib/swr/useIsMobile";
import { isEditableTarget } from "@app/lib/utils/editable_target";
import type { ConversationSidePanelType } from "@app/types/conversation_side_panel";
import {
  AGENT_ACTIONS_SIDE_PANEL_TYPE,
  CREDITS_SIDE_PANEL_TYPE,
  DETAILS_SIDE_PANEL_TYPE,
  FILE_PREVIEW_SIDE_PANEL_TYPE,
  FILES_SIDE_PANEL_TYPE,
  FULL_SCREEN_HASH_PARAM,
  filePreviewTargetFrom,
  INTERACTIVE_CONTENT_SIDE_PANEL_TYPE,
  LIVE_DOC_SIDE_PANEL_TYPE,
  PLAN_SIDE_PANEL_TYPE,
  SIDE_PANEL_HASH_PARAM,
  SIDE_PANEL_TYPE_HASH_PARAM,
  SKILL_SIDE_PANEL_TYPE,
  serializeFilePreviewTarget,
  TOOL_REVIEW_SIDE_PANEL_TYPE,
} from "@app/types/conversation_side_panel";
import { assertNever } from "@app/types/shared/utils/assert_never";
import React, { useCallback, useEffect, useMemo } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";

type OpenPanelParams =
  | {
      type: "actions";
      messageId: string;
      actionId?: string;
    }
  | {
      type: "interactive_content";
      fileId: string;
      timestamp?: string;
    }
  | {
      type: "file_preview";
      fileId?: string;
      filePath?: string;
    }
  | {
      type: "files";
    }
  | {
      type: "credits";
    }
  | {
      type: "plan";
    }
  | {
      type: "skill";
      skillId: string;
    }
  | {
      type: "tool_review";
      actionId: string;
    }
  | {
      type: "details";
    }
  | {
      type: "live_doc";
      docId: string;
    };

const isSupportedPanelType = (
  type: string | undefined
): type is ConversationSidePanelType =>
  type === "actions" ||
  type === "artifact_preview" ||
  type === "credits" ||
  type === "interactive_content" ||
  type === "file_preview" ||
  type === "files" ||
  type === "plan" ||
  type === "skill" ||
  type === "tool_review" ||
  type === "details" ||
  type === "live_doc";

interface ConversationSidePanelContextType {
  currentPanel: ConversationSidePanelType;
  openPanel: (params: OpenPanelParams) => void;
  togglePanel: (params: OpenPanelParams) => void;
  closePanel: () => void;
  onPanelClosed: () => void;
  setPanelRef: (ref: ImperativePanelHandle | null) => void;
  panelRef: React.MutableRefObject<ImperativePanelHandle | null>;
  setVirtuosoMsg: (msg: AgentMessageWithStreaming) => void;
  virtuosoMsg: AgentMessageWithStreaming | null;
  data: string | undefined;
  hasPanelHost: boolean;
  registerPanelHost: () => () => void;
  backToDetails: (() => void) | undefined;
}

export const ConversationSidePanelContext = React.createContext<
  ConversationSidePanelContextType | undefined
>(undefined);

export function useConversationSidePanelContext() {
  const context = React.useContext(ConversationSidePanelContext);
  if (!context) {
    throw new Error(
      "useConversationSidePanelContext must be used within a ConversationSidePanelProvider"
    );
  }

  return context;
}

export function parseDataAsMessageIdAndActionId(data?: string): {
  messageId?: string;
  actionId?: string;
} {
  // data can be "messageId" or "messageId@actionId" for single-action view.
  // TODO: Clean up once inline activity is rolled out -- the single-action view
  // should fetch only the action it needs, not the full message.
  const [messageId, actionId] = data?.includes("@")
    ? data.split("@")
    : [data, undefined];

  return { messageId, actionId };
}

const detailsPanelClosedPreference = { open: false };

interface ConversationSidePanelProviderProps {
  children: React.ReactNode;
  /** Remembers per workspace whether the "In this chat" panel was left open; omit to not persist. */
  detailsPanelPreferenceKey?: string;
}

export function ConversationSidePanelProvider({
  children,
  detailsPanelPreferenceKey,
}: ConversationSidePanelProviderProps) {
  const [data, setData] = useHashParam(SIDE_PANEL_HASH_PARAM);
  const [currentPanel, setCurrentPanel] = useHashParam(
    SIDE_PANEL_TYPE_HASH_PARAM
  );
  const [fullScreenHash, setFullScreenHash] = useHashParam(
    FULL_SCREEN_HASH_PARAM
  );
  const activeConversationId = useActiveConversationId();
  const previousConversationIdRef = React.useRef(activeConversationId);

  const panelRef = React.useRef<ImperativePanelHandle | null>(null);
  const [virtuosoMsg, setVirtuosoMsg] =
    React.useState<AgentMessageWithStreaming | null>(null);
  const [panelHostCount, setPanelHostCount] = React.useState(0);
  const registerPanelHost = useCallback(() => {
    setPanelHostCount((count) => count + 1);
    return () => setPanelHostCount((count) => count - 1);
  }, []);
  const isMobile = useIsMobile();
  const { value: detailsPanelPreference, setValue: setDetailsPanelPreference } =
    useScopedPodUiPreferences({
      scope: "conversationDetailsPanel",
      resourceId: detailsPanelPreferenceKey,
      defaultValue: detailsPanelClosedPreference,
    });
  const [openedFromDetails, setOpenedFromDetails] = React.useState(false);
  const [openedFromFiles, setOpenedFromFiles] = React.useState(false);
  const lastAutoOpenConversationIdRef = React.useRef<string | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  const setPanelRef = useCallback(
    (ref: ImperativePanelHandle | null) => {
      panelRef.current = ref;
    },
    [panelRef]
  );

  // This should be called once the closing animation is done (onTransitionEnd)
  // so you won't have content flickering
  const onPanelClosed = useCallback(() => {
    setData(undefined);
    setCurrentPanel(undefined);
    setOpenedFromDetails(false);
    setOpenedFromFiles(false);
  }, [setData, setCurrentPanel]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: ignored using `--suppress`
  const collapsePanel = useCallback(() => {
    if (panelRef && panelRef.current) {
      panelRef.current.collapse();
    } else {
      // in case there is no ref found (agent builder preview), close the panel directly
      onPanelClosed();
    }
  }, [panelRef, onPanelClosed]);

  // User-initiated close. Programmatic closes (conversation switch, hash sync) use
  // `collapsePanel` so they don't overwrite the remembered "In this chat" preference.
  const closePanel = useCallback(() => {
    if (currentPanel === DETAILS_SIDE_PANEL_TYPE || openedFromDetails) {
      setDetailsPanelPreference(detailsPanelClosedPreference);
    }
    collapsePanel();
  }, [
    currentPanel,
    openedFromDetails,
    setDetailsPanelPreference,
    collapsePanel,
  ]);

  // Full screen owns Escape while active (`usePanelFullScreen`): the first
  // Escape leaves full screen, the next one closes the panel. Open menus and
  // dialogs prevent default on their own Escape, and typing keeps the panel.
  const isPanelFullScreen = fullScreenHash === "true";
  useEffect(() => {
    if (!currentPanel || isPanelFullScreen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== "Escape" ||
        event.defaultPrevented ||
        isEditableTarget(event.target)
      ) {
        return;
      }
      closePanel();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [currentPanel, isPanelFullScreen, closePanel]);

  // Shared selection; `toggle` decides whether re-selecting the shown panel closes it.
  const applyPanel = useCallback(
    (params: OpenPanelParams, { toggle }: { toggle: boolean }) => {
      setCurrentPanel(params.type);

      switch (params.type) {
        case AGENT_ACTIONS_SIDE_PANEL_TYPE: {
          const newData = params.actionId
            ? `${params.messageId}@${params.actionId}`
            : params.messageId;

          // A different message/action switches content; only the same data toggles closed.
          if (toggle && newData === data) {
            closePanel();
            return;
          }

          setData(newData);
          break;
        }

        case INTERACTIVE_CONTENT_SIDE_PANEL_TYPE:
          // eslint-disable-next-line no-unused-expressions
          params.timestamp
            ? setData(`${params.fileId}@${params.timestamp}`)
            : setData(params.fileId);
          break;

        case FILE_PREVIEW_SIDE_PANEL_TYPE: {
          const target = filePreviewTargetFrom(params);
          if (!target) {
            return;
          }
          setData(serializeFilePreviewTarget(target));
          break;
        }

        case FILES_SIDE_PANEL_TYPE:
          if (toggle && currentPanel === FILES_SIDE_PANEL_TYPE) {
            closePanel();
            return;
          }
          setData("files");
          break;

        case CREDITS_SIDE_PANEL_TYPE:
          if (toggle && currentPanel === CREDITS_SIDE_PANEL_TYPE) {
            closePanel();
            return;
          }
          setData("credits");
          break;

        case PLAN_SIDE_PANEL_TYPE:
          if (toggle && currentPanel === PLAN_SIDE_PANEL_TYPE) {
            closePanel();
            return;
          }
          setData("plan");
          break;

        case SKILL_SIDE_PANEL_TYPE:
          // A different skill switches content; only the same skill toggles closed.
          if (toggle && params.skillId === data) {
            closePanel();
            return;
          }
          setData(params.skillId);
          break;

        case TOOL_REVIEW_SIDE_PANEL_TYPE:
          if (toggle && params.actionId === data) {
            closePanel();
            return;
          }
          setData(params.actionId);
          break;

        case LIVE_DOC_SIDE_PANEL_TYPE:
          if (toggle && params.docId === data) {
            closePanel();
            return;
          }
          setData(params.docId);
          break;

        case DETAILS_SIDE_PANEL_TYPE:
          if (toggle && currentPanel === DETAILS_SIDE_PANEL_TYPE) {
            closePanel();
            return;
          }
          setData(DETAILS_SIDE_PANEL_TYPE);
          setDetailsPanelPreference({ open: true });
          break;

        default:
          assertNever(params);
      }

      setOpenedFromFiles(
        params.type === FILE_PREVIEW_SIDE_PANEL_TYPE &&
          currentPanel === FILES_SIDE_PANEL_TYPE
      );
      setOpenedFromDetails(
        params.type !== DETAILS_SIDE_PANEL_TYPE &&
          (currentPanel === DETAILS_SIDE_PANEL_TYPE ||
            (openedFromDetails && currentPanel !== undefined))
      );

      // Re-expand imperatively: the container's expand effect only fires when `currentPanel`
      // changes, so a close→reopen race (same value) wouldn't re-run it. No-op on mobile.
      panelRef.current?.expand(DEFAULT_RIGHT_PANEL_SIZE);
    },
    [
      setCurrentPanel,
      setData,
      data,
      closePanel,
      currentPanel,
      openedFromDetails,
      setDetailsPanelPreference,
    ]
  );

  // Idempotent open for programmatic callers: a toggle could mis-close during a close→reopen
  // transition where `currentPanel` still reads the old value.
  const openPanel = useCallback(
    (params: OpenPanelParams) => applyPanel(params, { toggle: false }),
    [applyPanel]
  );

  // Toggle for user-controlled buttons: re-selecting the shown panel closes it.
  const togglePanel = useCallback(
    (params: OpenPanelParams) => applyPanel(params, { toggle: true }),
    [applyPanel]
  );

  // Close the panel when switching conversations: the provider stays mounted
  // across navigation and useHashParam does not re-sync on pushState, so the
  // previous conversation's panel would otherwise stay open. Skips the initial
  // mount (deep links) and the null -> id transition (new conversation flow).
  useEffect(() => {
    const previousConversationId = previousConversationIdRef.current;
    previousConversationIdRef.current = activeConversationId;

    if (
      previousConversationId &&
      previousConversationId !== activeConversationId
    ) {
      // Exit full screen too, mirroring FrameRenderer's close button.
      setFullScreenHash(undefined);
      if (detailsPanelPreference.open && !isMobile) {
        openPanel({ type: DETAILS_SIDE_PANEL_TYPE });
      } else {
        collapsePanel();
      }
    }
  }, [
    activeConversationId,
    collapsePanel,
    openPanel,
    detailsPanelPreference.open,
    isMobile,
    setFullScreenHash,
  ]);

  // Reopen "In this chat" when entering a conversation without a deep-linked panel.
  useEffect(() => {
    if (
      !activeConversationId ||
      lastAutoOpenConversationIdRef.current === activeConversationId
    ) {
      return;
    }
    lastAutoOpenConversationIdRef.current = activeConversationId;
    if (detailsPanelPreference.open && !isMobile && !data) {
      openPanel({ type: DETAILS_SIDE_PANEL_TYPE });
    }
  }, [
    activeConversationId,
    detailsPanelPreference.open,
    isMobile,
    data,
    openPanel,
  ]);

  // Initialize panel state from URL hash parameters
  useEffect(() => {
    if (data && currentPanel) {
      setCurrentPanel(currentPanel);
    } else if (!data) {
      collapsePanel();
    }
  }, [data, currentPanel, setCurrentPanel, collapsePanel]);

  const backToDetails = useMemo(
    () =>
      (openedFromDetails ||
        currentPanel === INTERACTIVE_CONTENT_SIDE_PANEL_TYPE ||
        currentPanel === LIVE_DOC_SIDE_PANEL_TYPE ||
        (currentPanel === FILE_PREVIEW_SIDE_PANEL_TYPE && !openedFromFiles)) &&
      currentPanel !== DETAILS_SIDE_PANEL_TYPE
        ? () => openPanel({ type: DETAILS_SIDE_PANEL_TYPE })
        : undefined,
    [openedFromDetails, openedFromFiles, currentPanel, openPanel]
  );

  const value = useMemo(
    () => ({
      currentPanel: isSupportedPanelType(currentPanel)
        ? currentPanel
        : undefined,
      openPanel,
      togglePanel,
      closePanel,
      onPanelClosed,
      setPanelRef,
      panelRef,
      setVirtuosoMsg,
      virtuosoMsg,
      data,
      hasPanelHost: panelHostCount > 0,
      registerPanelHost,
      backToDetails,
    }),
    [
      backToDetails,
      currentPanel,
      openPanel,
      togglePanel,
      closePanel,
      onPanelClosed,
      setPanelRef,
      virtuosoMsg,
      data,
      panelHostCount,
      registerPanelHost,
    ]
  );

  return (
    <ConversationSidePanelContext.Provider value={value}>
      {children}
    </ConversationSidePanelContext.Provider>
  );
}
